import React, { useEffect, useState } from 'react';
import { ShieldAlert, Search, RefreshCw } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { AuditLogEntry } from '../../types';
import { getAuditLogs, getCurrentUserPermissions } from '../../utils/auth/authService';

const cardStyle = (border: string, background: string): React.CSSProperties => ({
  background,
  border: `1px solid ${border}`,
  borderRadius: 12,
});

const AuditLogsPage: React.FC = () => {
  const { theme } = useTheme();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [filters, setFilters] = useState({
    eventType: '',
    requestId: '',
    actorUserId: '',
    path: '',
  });

  const loadLogs = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const data = await getAuditLogs({
        limit: 100,
        eventType: nextFilters.eventType || undefined,
        requestId: nextFilters.requestId || undefined,
        actorUserId: nextFilters.actorUserId || undefined,
        path: nextFilters.path || undefined,
      });
      setLogs(data);
      setSelectedLog((current) => data.find((entry) => entry.event_id === current?.event_id) || data[0] || null);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      setLogs([]);
      setSelectedLog(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const profile = await getCurrentUserPermissions();
        const roleNames = profile.roles.map((role) => role.name);
        const canView = roleNames.includes('admin') || roleNames.includes('auditor');
        setAuthorized(canView);
        if (canView) {
          await loadLogs();
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load role profile:', error);
        setAuthorized(false);
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  if (loading && authorized === null) {
    return <div style={{ color: theme.text }}>Loading...</div>;
  }

  if (!authorized) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ ...cardStyle(theme.border, theme.cardBackground), padding: 24, maxWidth: 720 }}>
          <h1 style={{ marginTop: 0, color: theme.text }}>Audit Logs</h1>
          <p style={{ marginBottom: 0, color: theme.textSecondary }}>
            Access is limited to users with the <strong>admin</strong> or <strong>auditor</strong> role.
          </p>
        </div>
      </div>
    );
  }

  const selectedPayload = selectedLog ? JSON.stringify(selectedLog, null, 2) : '';

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 28, margin: 0 }}>Audit Logs</h1>
          <p style={{ color: theme.textSecondary, margin: '8px 0 0 0' }}>
            Read-only trace of inbound and outbound network activity, with signed event metadata.
          </p>
        </div>
        <button
          onClick={() => loadLogs()}
          style={{
            border: `1px solid ${theme.border}`,
            background: theme.cardBackground,
            color: theme.text,
            borderRadius: 8,
            padding: '10px 14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600,
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div style={{ ...cardStyle(theme.border, theme.cardBackground), padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
          {[
            ['eventType', 'Event type'],
            ['requestId', 'Request ID'],
            ['actorUserId', 'Actor user ID'],
            ['path', 'Request path'],
          ].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, color: theme.textSecondary, fontSize: 12, fontWeight: 600 }}>
              {label}
              <input
                value={filters[key as keyof typeof filters]}
                onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))}
                style={{
                  border: `1px solid ${theme.border}`,
                  background: theme.background,
                  color: theme.text,
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              />
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            onClick={() => loadLogs()}
            style={{
              border: 'none',
              background: theme.buttonBackground,
              color: theme.buttonText,
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 700,
            }}
          >
            <Search size={16} />
            Apply Filters
          </button>
          <button
            onClick={() => {
              const nextFilters = { eventType: '', requestId: '', actorUserId: '', path: '' };
              setFilters(nextFilters);
              loadLogs(nextFilters);
            }}
            style={{
              border: `1px solid ${theme.border}`,
              background: theme.cardBackground,
              color: theme.text,
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(460px, 1.2fr) minmax(380px, 1fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ ...cardStyle(theme.border, theme.cardBackground), overflow: 'hidden' }}>
          <div style={{ padding: 16, borderBottom: `1px solid ${theme.border}`, color: theme.text, fontWeight: 700 }}>
            Recent Events
          </div>
          {loading ? (
            <div style={{ padding: 20, color: theme.text }}>Loading...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 28, color: theme.textSecondary }}>No audit log entries matched the current filters.</div>
          ) : (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {logs.map((log) => {
                const isSelected = selectedLog?.event_id === log.event_id;
                return (
                  <button
                    key={log.event_id}
                    onClick={() => setSelectedLog(log)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: `1px solid ${theme.border}`,
                      background: isSelected ? theme.hoverBackground : 'transparent',
                      padding: 16,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <div style={{ color: theme.text, fontWeight: 700 }}>{log.event_type}</div>
                      <div style={{ color: theme.textSecondary, fontSize: 12 }}>
                        {new Date(log.occurred_at).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      {log.request?.method && (
                        <span style={{ padding: '3px 8px', borderRadius: 999, background: theme.background, color: theme.text, fontSize: 12, fontWeight: 700 }}>
                          {log.request.method}
                        </span>
                      )}
                      {typeof log.response?.status_code === 'number' && (
                        <span style={{ padding: '3px 8px', borderRadius: 999, background: theme.successLight, color: theme.successDark, fontSize: 12, fontWeight: 700 }}>
                          {log.response.status_code}
                        </span>
                      )}
                      {log.actor?.username && (
                        <span style={{ padding: '3px 8px', borderRadius: 999, background: theme.warningLight, color: theme.warningDark, fontSize: 12, fontWeight: 700 }}>
                          {log.actor.username}
                        </span>
                      )}
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: 13, marginBottom: 6 }}>
                      {log.request?.path || log.request?.route_path || 'No path recorded'}
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>
                      request_id: {log.request_id}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ ...cardStyle(theme.border, theme.cardBackground), overflow: 'hidden' }}>
          <div style={{ padding: 16, borderBottom: `1px solid ${theme.border}`, color: theme.text, fontWeight: 700 }}>
            Event Details
          </div>
          {!selectedLog ? (
            <div style={{ padding: 24, color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: 10 }}>
              <ShieldAlert size={18} />
              Select an event to inspect the full audit payload.
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
                {[
                  ['Event ID', selectedLog.event_id],
                  ['Request ID', selectedLog.request_id],
                  ['Actor', selectedLog.actor?.username || selectedLog.actor?.user_id || 'anonymous'],
                  ['Outcome', selectedLog.outcome?.result || String(selectedLog.response?.status_code ?? 'n/a')],
                  ['Method', selectedLog.request?.method || 'n/a'],
                  ['Path', selectedLog.request?.path || selectedLog.request?.route_path || 'n/a'],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: theme.background, borderRadius: 8, padding: 12 }}>
                    <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>{label}</div>
                    <div style={{ color: theme.text, fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
                  </div>
                ))}
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: 16,
                  background: '#0f172a',
                  color: '#e2e8f0',
                  borderRadius: 10,
                  overflowX: 'auto',
                  fontSize: 12,
                  lineHeight: 1.5,
                  maxHeight: '50vh',
                }}
              >
                {selectedPayload}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogsPage;
